import React from 'react'

import {useFormik} from 'formik'
import difference from 'lodash/difference'
import flatMap from 'lodash/flatMap'
import omit from 'lodash/omit'
import {replace} from 'redux-first-history'

import * as coreActions from '../../actions/core'
import * as navigationActions from '../../actions/navigation'

import useAsync from '../../../../commons/src/hooks/use-async'
import useDispatch from '../../hooks/use-dispatch'
import * as optionApi from '../../services/api/option'
import * as providerInfoApi from '../../services/api/provider-info'
import * as providerInfoOptionApi from '../../services/api/provider-info-option'
import {
  MixedValue,
  isMixedValue,
  toPossiblyMixedValue,
  toStringValue,
  toTouchedFormData,
} from '../utils/form'

import {
  EditProviderInfoTab,
  OptionId,
  OptionWithLinkedProviderInfos,
  ProviderId,
  ProviderInfo,
  ProviderInfoId,
} from '../../types'

import SaveButtonPartial from '../save-button-partial'

import ProductionTimesFormPartial, {
  MixedFormData,
  SingleFormData,
  createValidationSchemaWithProductionTimes,
  formDataToPartialRequest,
  formatProductionTimeName,
  initialValues,
  sortProductionTimes,
  validateProductionTimeOption,
} from './production-times-form-partial'

type Props = {
  providerId: ProviderId
  providerInfos: Array<ProviderInfo>
}

const providerInfosToFormData = (
  providerInfos: ProviderInfo[],
  productionTimeOptions: OptionWithLinkedProviderInfos[]
): MixedFormData => ({
  productionTimeFast: toPossiblyMixedValue(
    providerInfos.map(providerInfo => providerInfo.productionTimeFast),
    toStringValue
  ),
  productionTimeSlow: toPossiblyMixedValue(
    providerInfos.map(providerInfo => providerInfo.productionTimeSlow),
    toStringValue
  ),
  productionTimeIds: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      productionTimeOptions
        .filter(({providerInfos: productionTimeProviderInfos}) =>
          productionTimeProviderInfos.map(({id}) => id).includes(providerInfo.id)
        )
        .map(({id}) => id)
    ),
    ids => ids
  ),
})

const updateLinkedProductionTimes = (
  initialProductionTimeIds: OptionId[],
  nextProductionTimeIds: OptionId[],
  providerInfoIds: ProviderInfoId[]
) => {
  const addedProductionTimeIds = difference(nextProductionTimeIds, initialProductionTimeIds)
  const removedProductionTimeIds = difference(initialProductionTimeIds, nextProductionTimeIds)

  // Unlink providerInfos from removed production time options
  const unlinkPromises = flatMap(removedProductionTimeIds, optionId =>
    providerInfoIds.map(providerInfoId =>
      providerInfoOptionApi.remove({
        providerInfoId,
        optionId,
      })
    )
  )

  // Add provider Infos to newly added production time options
  const linkPromises = flatMap(addedProductionTimeIds, optionId =>
    providerInfoIds.map(providerInfoId => providerInfoOptionApi.create({providerInfoId, optionId}))
  )

  return Promise.all([...unlinkPromises, ...linkPromises])
}

const EditProductionTimesPartial = ({providerId, providerInfos}: Props) => {
  const dispatch = useDispatch()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const {
    isLoading: isProductionTimesLoading,
    data: productionTimes = [],
    refresh: refreshProductionTimes,
  } = useAsync(
    () => optionApi.getProductionTimeOptionsWithLinkedProviderInfosByProvider(providerId),
    [],
    {
      onError: error => dispatch(coreActions.fatalError(error)),
    }
  )

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...providerInfosToFormData(providerInfos, sortProductionTimes(productionTimes)),
    },
    validationSchema: createValidationSchemaWithProductionTimes(productionTimes),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(
        values,
        formik.initialValues
      ) as Partial<SingleFormData>
      const providerInfoRequest = formDataToPartialRequest(
        omit(touchedValues, ['productionTimeIds'])
      )

      try {
        const providerInfoIds = providerInfos.map(info => info.id)

        if (touchedValues.productionTimeIds) {
          if (!isMixedValue(formik.initialValues.productionTimeIds)) {
            const initialProductionTimeIds = formik.initialValues.productionTimeIds

            await updateLinkedProductionTimes(
              initialProductionTimeIds,
              touchedValues.productionTimeIds as OptionId[],
              providerInfoIds
            )
          } else {
            const updateLinkedProductionTimesPromises = flatMap(
              providerInfos,
              async (providerInfo, index) => {
                const initialProductionTimeIds = (
                  formik.initialValues.productionTimeIds as MixedValue<OptionId[]>
                ).values[index]

                return updateLinkedProductionTimes(
                  initialProductionTimeIds,
                  touchedValues.productionTimeIds as OptionId[],
                  [providerInfo.id]
                )
              }
            )
            await Promise.all(updateLinkedProductionTimesPromises)
          }
          refreshProductionTimes()
        }

        const nextProviderInfos = await providerInfoApi.patchMultiple(
          providerInfoRequest,
          providerInfoIds
        )

        dispatch(
          navigationActions.goToEditProviderInfo(
            nextProviderInfos,
            EditProviderInfoTab.PRODUCTION_TIME,
            replace
          )
        )

        setIsSubmitted(true)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const productionTimeOptions = sortProductionTimes(productionTimes).map(productionTime => {
    const isValid = validateProductionTimeOption(productionTime, formik.values)
    return {
      label: formatProductionTimeName(productionTime, !isValid),
      value: productionTime.id,
      disabled: !isValid,
    }
  })

  return (
    <ProductionTimesFormPartial
      formik={formik}
      onRefresh={refreshProductionTimes}
      submitButton={<SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />}
      isLoading={isProductionTimesLoading}
      options={productionTimeOptions}
      providerInfos={providerInfos}
    />
  )
}

export default EditProductionTimesPartial
